import React from 'react'
import moment from 'moment'
import { TouchableOpacity, View } from 'react-native'
import { useNavigation } from '@react-navigation/native'
import { useSelector } from 'react-redux'
import { StackNavigationProp } from '@react-navigation/stack'
import { Feature, useFeatureFlag } from '@src/components'
import { Icon, Text } from '@src/components/base'
import { StyleService, useStyleSheet } from '@src/style/service'
import { Storage } from '@src/utils'
import { useStorageValue } from '@src/utils/storage'
import { NotificationEngineScreen } from '@src/types.ts'
import { AppStackParamList } from '@navigation/types'
import { useNotifications } from '@components/notifications/utils/hooks.ts'
import { userPropertiesSelector, userSelector } from '@selectors/app.ts'
import { uiFlagsSelector } from '@selectors/uiFlags'
import { Analytics, CustomEventTypes } from '@config'
import { DATE_FORMAT } from '@src/config/momentFormat'

export const VideoCallsUpsellBanner = () => {
  const user = useSelector(userSelector)
  const userId = user?.id
  const primaryCoachingKind = user?.primaryCoachingKind
  const showInsuranceUi = primaryCoachingKind?.insuranceReimbursable === true
  const { insuranceInformationSubmitted } = useSelector(uiFlagsSelector)
  const { insuranceSurveySkipReason } = useSelector(userPropertiesSelector) ?? {}
  const navigation = useNavigation<StackNavigationProp<AppStackParamList>>()
  const styles = useStyleSheet(themedStyles)
  const [visitedCount, setVisitedCount] = useStorageValue<number>(
    `${Storage.VIDEO_CALLS_UPSELL_MODAL_VISITED_COUNT_KEY}_${userId}`,
  )
  const [upsellModalCompleted] = useStorageValue<boolean>(
    `${Storage.VIDEO_CALLS_UPSELL_MODAL_COMPLETED_KEY}_${userId}`,
  )
  const [checklistBannerVisited] = useStorageValue(
    Storage.ONBOARDING_CHECKLIST_REMINDER_VISITED_KEY,
  )

  const isNotificationInboxEnabled = useFeatureFlag(Feature.NotificationInbox)
  const startDate = moment().format(DATE_FORMAT)
  const notifications = useNotifications(NotificationEngineScreen.Home, startDate)

  // `insuranceInformationSubmitted` comes from a `Partial<UiFlags>`, so it can be
  // `undefined` until `uiFlags/fetch` completes - treat the unknown case as
  // "don't show the banner yet" so we don't briefly flash it for users who
  // already submitted on web.
  const shouldShow =
    showInsuranceUi &&
    !isNotificationInboxEnabled &&
    !notifications.length &&
    !!checklistBannerVisited &&
    !!upsellModalCompleted &&
    !!visitedCount &&
    visitedCount < 3 &&
    insuranceInformationSubmitted === false &&
    insuranceSurveySkipReason?.value !== 'no_insurance'

  if (!shouldShow) {
    return null
  }

  const onPress = () => {
    navigation.navigate('VideoCallsUpsellDetailsModal')
    Analytics.track(CustomEventTypes.VideoCallsUpsellBannerClicked, {
      userId,
      type: 'upsell_modal',
    })

    setVisitedCount((visitedCount || 0) + 1)
  }

  return (
    <TouchableOpacity
      accessibilityLabel="Insurance covered video calls"
      onPress={onPress}
      style={styles.container}
    >
      <View style={styles.iconContainer}>
        <Icon name="gift" size="20" weight="bold" style={styles.icon} />
      </View>
      <View style={styles.textContainer}>
        <Text type="large" bold>
          Insurance covered video calls
        </Text>
        <Text type="regular" style={styles.secondaryText}>
          Tap to learn more!
        </Text>
      </View>
      <Icon name="caret-right" size="16" weight="bold" style={styles.caretIcon} />
    </TouchableOpacity>
  )
}

const themedStyles = StyleService.create({
  container: {
    marginTop: 8,
    padding: 16,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'theme.surface.banner',
  },
  iconContainer: {
    padding: 16,
    borderRadius: 56,
    backgroundColor: 'theme.primary.base',
    marginRight: 16,
  },
  icon: {
    color: 'theme.solid.white',
  },
  textContainer: {
    flex: 1,
  },
  secondaryText: {
    color: 'theme.text.secondary',
  },
  caretIcon: {
    color: 'theme.surface.base0',
  },
})
